// Which cart lines did the backend refuse to price?
//
// /cart/preview does NOT fail when it cannot sell a line. `ops_receipt.add_items`
// collects the per-line rejection into `errors`, the handler re-shapes those into
// `warnings[]`, and the response comes back 200 with the offending line simply
// ABSENT from `items` and its money absent from `totals`. Until this module
// existed nothing in the POS read `warnings`, so the cashier saw a cart row with
// its catalog price (drawn from local state) sitting above a Subtotal, Tax, Total
// and Checkout button that all read $0.00, with no explanation anywhere — then a
// 400 `cart_contains_invalid_items` at the moment of tender, because /cart/commit
// hard-rejects what preview merely warned about.
//
// That went live chain-wide when the clerk-POS oversell gate shipped
// (waio-portal PR #2256, 2026-09-15): every product whose count-aware on-hand
// sums to <= 0 now gets refused, which at One Plant Pickering alone is ~81 SKUs
// that still sold in the last fortnight.
//
// ATTRIBUTION IS THE WHOLE PROBLEM. The BE drops the product id on the way out:
//
//     for err in (result.get('errors') or []):
//         warnings.append({'kind': 'ops_error', 'detail': str(err)})
//
// `err` is `over_err['error']` — the message STRING — so the `product_id` that
// `_over_on_hand_error` returned alongside it never reaches the wire. We
// therefore work backwards from which lines came back unpriced, and lean on two
// guarantees to pair them up:
//
//   * `add_items` iterates the items in the order we sent them and appends
//     errors in that same order, and we send lines in cart order — so the k-th
//     unattributed warning belongs to the k-th unpriced line;
//   * a line that IS priced is never blocked, whatever the warnings say (a
//     cannabis-limit warning is advisory and rides alongside a priced cart).
//
// Deliberately conservative: with no warnings at all we report nothing and the
// caller keeps today's "leave it pending" behaviour, so a transient shape
// mismatch can never turn into a red row on a cart the BE was perfectly happy
// with.
//
// Pure + dependency-free (`.core` convention) so `node --test` can run it.

/** One entry of /cart/preview's `warnings[]`. Everything is optional because
 *  only `kind` is guaranteed and only `ops_error` reliably carries `detail`. */
export interface PreviewWarningLike {
  kind?: string
  product_id?: number | null
  detail?: string | null
  available?: number | null
  requested?: number | null
}

/** One row of /cart/preview's `items[]`, reduced to what identifies it. The BE
 *  returns the resolved lot as `batch_number`; older responses used
 *  `lot_number`. */
export interface PricedRowLike {
  product_id: number
  batch_number?: string | null
  lot_number?: string | null
  /** Units the BE actually priced on this row. Load-bearing: it is what
   *  separates "the BE merged my two lines into one row" from "the BE priced
   *  one of my two lines and refused the other". */
  qty?: number | string | null
}

/** One line of the local cart, reduced to what identifies it. */
export interface CartLineLike {
  id: string
  product_id: number
  lot_number?: string | null
  /** Units on the local line. Defaults to 1 when absent. */
  qty?: number | string | null
  /** Basket SKUs explode server-side into their child rows, so the basket line
   *  itself is legitimately absent from `items[]`. Never infer a block from
   *  one — only an explicit product_id warning can flag a basket. */
  basket_id?: number | null
}

export interface BlockedLine {
  id: string
  product_id: number
  /** Cashier-facing reason, straight from the BE where it gave us one. */
  message: string
}

export interface BlockedCartResult {
  /** In cart order. */
  blocked: BlockedLine[]
  /** Warnings that belong to no visible line — show them at cart level. */
  notices: string[]
  /**
   * The cart CANNOT commit. Mirrors the backend's own commit condition rather
   * than our attribution: /cart/commit rejects with 400
   * `cart_contains_invalid_items` whenever `add_items` returned any `errors`,
   * and those are exactly the warnings that reach us as `ops_error`.
   *
   * Kept separate from `blocked` on purpose. Attribution is inferred and can
   * come up empty — a basket's CHILD sku being refused produces an `ops_error`
   * for a product that has no cart row of its own, so no row can go red. The
   * tender must still be blocked, or the cashier walks into the 400 with the
   * customer's card already in hand. (`out_of_stock` is excluded: it comes from
   * `not_on_hand_items`, which is kiosk-only and does not fail commit.)
   */
  hardRejection: boolean
}

const GENERIC = 'This item can’t be sold right now.'

/** Cashier-facing sentence for one warning. */
export function warningText(w: PreviewWarningLike): string {
  const detail = typeof w.detail === 'string' ? w.detail.trim() : ''
  // `ops_error` details are already written for the cashier by the BE guard
  // ("Cannot add 1 units. Only 0 units available (...)"), so pass them through.
  // `out_of_stock` ships the useless literal 'product not on hand' alongside
  // real numbers, so prefer the numbers when we have them.
  if (w.kind === 'out_of_stock') {
    const available = toFiniteNumber(w.available)
    const requested = toFiniteNumber(w.requested)
    if (available !== null && requested !== null) {
      return `Only ${fmtQty(available)} in stock — ${fmtQty(requested)} requested.`
    }
    if (available !== null) return `Only ${fmtQty(available)} in stock.`
    return detail || 'Out of stock.'
  }
  if (detail) return detail
  if (w.kind === 'cannabis_limit') return 'Over the customer’s cannabis limit.'
  return GENERIC
}

function toFiniteNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  return Number.isFinite(n) ? n : null
}

function fmtQty(n: number): string {
  // Trim the BE's trailing zeros: 1.00000 -> 1, 0.50000 -> 0.5.
  return String(Math.round(n * 1000) / 1000)
}

function lotKey(product_id: number, lot: string | null | undefined): string {
  return `${product_id}|${lot ?? ''}`
}

/**
 * Pair unpriced cart lines with the warnings that explain them.
 *
 * Matching mirrors `applyPreviewToLines` exactly — exact (product, lot) key
 * first, then product_id alone when the product has exactly one priced row —
 * so the two never disagree about whether a line came back priced. (Lines the
 * BE consolidated, e.g. `pos.receipt.group_items` merging two rows of the same
 * SKU, resolve to that single priced row and are correctly NOT blocked.)
 */
export function blockedCartLines(
  lines: CartLineLike[],
  pricedRows: PricedRowLike[],
  warnings: PreviewWarningLike[],
): BlockedCartResult {
  const hardRejection = warnings.some((w) => w.kind === 'ops_error')
  if (!warnings.length) return { blocked: [], notices: [], hardRejection }

  const byKey = new Set<string>()
  const countByProduct = new Map<number, number>()
  // Units priced vs units in the cart, per product. `null` marks a product
  // whose priced rows didn't all carry a usable qty, so coverage is unknowable
  // and we fall back to the count heuristic below.
  const pricedQty = new Map<number, number | null>()
  const cartQty = new Map<number, number>()
  for (const row of pricedRows) {
    byKey.add(lotKey(row.product_id, row.batch_number ?? row.lot_number ?? null))
    countByProduct.set(row.product_id, (countByProduct.get(row.product_id) ?? 0) + 1)
    const q = toFiniteNumber(row.qty)
    const running = pricedQty.get(row.product_id)
    pricedQty.set(
      row.product_id,
      q === null || running === null ? null : (running ?? 0) + Math.abs(q),
    )
  }
  for (const line of lines) {
    const q = toFiniteNumber(line.qty)
    cartQty.set(line.product_id, (cartQty.get(line.product_id) ?? 0) + Math.abs(q ?? 1))
  }

  const isPriced = (line: CartLineLike): boolean => {
    // Product fully accounted for by units: everything the cashier rang for it
    // made it into the receipt, however the BE redistributed the lots. Covers
    // both `pos.receipt.group_items` merging two lines into one row and the BE
    // substituting a different batch than the one the FE pinned.
    const covered = pricedQty.get(line.product_id)
    const wanted = cartQty.get(line.product_id) ?? 0
    if (covered !== undefined && covered !== null) {
      if (covered >= wanted) return true
      // Short on units => at least one line for this product was refused. The
      // exact (product, lot) key is then the only trustworthy signal; the
      // one-candidate fallback below would wrongly absolve the refused line.
      return byKey.has(lotKey(line.product_id, line.lot_number ?? null))
    }
    if (byKey.has(lotKey(line.product_id, line.lot_number ?? null))) return true
    return countByProduct.get(line.product_id) === 1
  }

  const unpriced = lines.filter((line) => !isPriced(line))
  const order = new Map(lines.map((line, i) => [line.id, i]))
  const claimed = new Map<string, string>()
  const unattributed: PreviewWarningLike[] = []

  // Pass 1 — warnings that name their product. `out_of_stock` always has,
  // and `ops_error` does too once the backend stops discarding the id.
  //
  // A named warning is never handed to pass 2. If the backend told us the
  // product and no visible line matches, we KNOW it is not one of these rows
  // — a basket's child sku, say — and guessing a row for it would redden a
  // line the cashier can see over a refusal that was about something they
  // cannot. It becomes a notice instead; `hardRejection` still blocks the
  // tender either way, so nothing is lost by declining to guess.
  const orphaned: PreviewWarningLike[] = []
  for (const w of warnings) {
    const pid = toFiniteNumber(w.product_id)
    if (pid === null) {
      unattributed.push(w)
      continue
    }
    const hits = unpriced.filter((l) => l.product_id === pid && !claimed.has(l.id))
    if (!hits.length) {
      orphaned.push(w)
      continue
    }
    for (const line of hits) claimed.set(line.id, warningText(w))
  }

  // Pass 2 — positional pairing, in cart order, for the ANONYMOUS ones only.
  // This is the legacy path: it exists because the backend used to ship
  // `{'kind': 'ops_error', 'detail': str(err)}` with the product id thrown
  // away, leaving the order the errors arrive in as the only signal. Baskets
  // sit it out: their line is absent by design, so consuming a warning on one
  // would both mislabel the basket and starve the line it was really about.
  const queue = [...unattributed]
  for (const line of unpriced) {
    if (claimed.has(line.id)) continue
    if (line.basket_id !== null && line.basket_id !== undefined) continue
    const w = queue.shift()
    if (!w) break
    claimed.set(line.id, warningText(w))
  }

  const blocked: BlockedLine[] = [...claimed.entries()]
    .map(([id, message]) => {
      const line = lines.find((l) => l.id === id)!
      return { id, product_id: line.product_id, message }
    })
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))

  return {
    blocked,
    notices: [...orphaned, ...queue].map(warningText),
    hardRejection,
  }
}
